// Оркестратор проводника (аудит, этап 2 — декомпозиция). Разнесён по модулям:
// onboarding-session (сессия/телеметрия/seen), onboarding-flow-data (экраны,
// шаги, host-markup), onboarding-assistant (аватар/live/clarify, late-bound
// хост). Здесь остаются состояние флоу, рендер экранов, обработка действий и
// публичный API (регистрация в shared/onboarding-gateway).
import './onboarding.css';
import { $ } from '../shared/index';
import {
  configureOnboardingRuntime,
  finishVideoScreen,
  shouldShowVideoExplainer,
  type OnboardingRuntimeConfig,
} from './onboarding-presenter';
import {
  WIZARD_STEPS,
  SUMMARY_LABELS,
  escapeHtml,
  buildHostMarkup,
  type OnboardingScreen,
} from './onboarding-flow-data';
import {
  configureOnboardingSessionTimeout,
  getOnboardingSessionTimeoutMin,
  touchOnboardingActivity,
  resetOnboardingSession,
  readLastActivityMs,
  getOnboardingSessionId,
  trackOnboardingEvent,
  isOnboardingSeen,
  markOnboardingSeen,
  shouldShowOnboarding,
} from './onboarding-session';
import {
  configureAssistantHost,
  syncAvatarReplica,
  submitLiveDialogQuestion,
  submitClarifyQuestion,
  startLiveVoiceInput,
} from './onboarding-assistant';
import { registerOnboardingGateway, type OnboardingAnswers } from '../shared/onboarding-gateway';

export type { OnboardingRuntimeConfig };
export { configureOnboardingRuntime };
export type { OnboardingAnswers } from '../shared/onboarding-gateway';
export type { OnboardingScreen } from './onboarding-flow-data';
export {
  configureOnboardingSessionTimeout,
  getOnboardingSessionId,
  isOnboardingSeen,
  markOnboardingSeen,
  shouldShowOnboarding,
} from './onboarding-session';

const ONBOARDING_DRAFT_KEY = 'ae3_onboarding_draft';

function resolveLastKnownStep(): string {
  if (state.screen === 'wizard') {
    return WIZARD_STEPS[state.stepIndex]?.key || 'wizard';
  }
  return state.screen;
}

function checkOnboardingSessionTimeout(): boolean {
  const lastActivity = readLastActivityMs();
  if (!lastActivity) return false;

  const timeoutMs = getOnboardingSessionTimeoutMin() * 60 * 1000;
  if (Date.now() - lastActivity <= timeoutMs) return false;

  void trackOnboardingEvent({
    event_name: 'drop_off',
    last_step: resolveLastKnownStep(),
    step_index: state.screen === 'wizard' ? state.stepIndex : undefined,
    drop_reason: 'timeout',
  });

  resetOnboardingSession();
  clearDraft();
  state.screen = 'welcome';
  state.previousScreen = 'welcome';
  state.stepIndex = 0;
  return true;
}

type OnboardingCallbacks = {
  onExitToCabinet: () => void;
  onDraftComplete?: (answers: OnboardingAnswers) => void | Promise<void>;
};

let active = false;
let hostEl: HTMLElement | null = null;
let callbacks: OnboardingCallbacks | null = null;

const state = {
  screen: 'welcome' as OnboardingScreen,
  previousScreen: 'welcome' as OnboardingScreen,
  stepIndex: 0,
  answers: loadDraftAnswers(),
};

function loadDraftAnswers(): OnboardingAnswers {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_DRAFT_KEY);
    if (!raw) return { fact: '', reaction: '', hypothesis: '', action: '' };
    const parsed = JSON.parse(raw) as Partial<OnboardingAnswers>;
    return {
      fact: parsed.fact || '',
      reaction: parsed.reaction || '',
      hypothesis: parsed.hypothesis || '',
      action: parsed.action || '',
    };
  } catch {
    return { fact: '', reaction: '', hypothesis: '', action: '' };
  }
}

function persistDraft() {
  sessionStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify(state.answers));
}

function clearDraft() {
  state.answers = { fact: '', reaction: '', hypothesis: '', action: '' };
  state.stepIndex = 0;
  sessionStorage.removeItem(ONBOARDING_DRAFT_KEY);
}

export function isOnboardingActive(): boolean {
  return active;
}

function setContainerMode(on: boolean) {
  const container = document.querySelector('.journey-container');
  container?.classList.toggle('onboarding-active', on);
}

function showScreen(name: OnboardingScreen) {
  state.screen = name;
  hostEl?.querySelectorAll<HTMLElement>('[data-onboarding-screen]').forEach((el) => {
    el.hidden = el.dataset.onboardingScreen !== name;
  });
  const globalActions = hostEl?.querySelector<HTMLElement>('#onboarding-global-actions');
  if (globalActions) {
    globalActions.hidden = ['welcome', 'pause', 'video'].includes(name);
  }
  hostEl?.focus({ preventScroll: true });
}

function renderWelcome() {
  showScreen('welcome');
  syncAvatarReplica('welcome');
}

function renderVideo() {
  showScreen('video');
}

function leaveVideoScreen(skipped: boolean) {
  finishVideoScreen(hostEl!, skipped);
  if (skipped) {
    void trackOnboardingEvent({ event_name: 'skip', skip_type: 'video' });
  }
  renderWelcome();
}

function handleVideoLoadError() {
  void trackOnboardingEvent({ event_name: 'skip', skip_type: 'video' });
  renderWelcome();
}

function renderExample() {
  showScreen('example');
  syncAvatarReplica('example');
}

function renderWizard() {
  const step = WIZARD_STEPS[state.stepIndex];
  const contextEl = hostEl?.querySelector<HTMLElement>('#onboarding-wizard-context');
  const titleEl = hostEl?.querySelector<HTMLElement>('#onboarding-wizard-title');
  const leadEl = hostEl?.querySelector<HTMLElement>('#onboarding-wizard-lead');
  const helpEl = hostEl?.querySelector<HTMLElement>('#onboarding-wizard-help-body');
  const textarea = hostEl?.querySelector<HTMLTextAreaElement>('#onboarding-wizard-answer');
  const progressLabel = hostEl?.querySelector<HTMLElement>('#onboarding-progress-label');
  const dots = hostEl?.querySelectorAll<HTMLElement>('.onboarding-progress-dot');

  if (contextEl) contextEl.textContent = step.context;
  if (titleEl) titleEl.textContent = step.title;
  if (leadEl) leadEl.textContent = step.lead;
  if (helpEl) helpEl.innerHTML = step.helpHtml;
  if (textarea) {
    textarea.placeholder = step.placeholder;
    textarea.value = state.answers[step.key] || '';
  }
  if (progressLabel) progressLabel.textContent = `Шаг ${state.stepIndex + 1} из 4`;
  dots?.forEach((dot, idx) => {
    dot.classList.toggle('active', idx === state.stepIndex);
    dot.classList.toggle('done', idx < state.stepIndex);
  });

  const errorEl = hostEl?.querySelector<HTMLElement>('#onboarding-wizard-error');
  if (errorEl) errorEl.textContent = '';

  const helpDetails = hostEl?.querySelector<HTMLDetailsElement>('#onboarding-wizard-help');
  if (helpDetails) helpDetails.open = false;

  showScreen('wizard');
  syncAvatarReplica('wizard');
  window.setTimeout(() => textarea?.focus(), 0);
}

function displayAnswer(value: string): string {
  return value || 'Пропущено — к этому можно вернуться позже.';
}

function renderSummary() {
  const container = hostEl?.querySelector<HTMLElement>('#onboarding-summary-content');
  if (!container) return;

  container.innerHTML = (Object.keys(SUMMARY_LABELS) as Array<keyof OnboardingAnswers>)
    .map((key) => {
      return `
        <div class="onboarding-summary-row">
          <strong>${escapeHtml(SUMMARY_LABELS[key])}</strong>
          <p>${escapeHtml(displayAnswer(state.answers[key]))}</p>
        </div>
      `;
    })
    .join('');

  showScreen('summary');
  syncAvatarReplica('summary');
}

function saveCurrentStepAnswer() {
  const step = WIZARD_STEPS[state.stepIndex];
  const textarea = hostEl?.querySelector<HTMLTextAreaElement>('#onboarding-wizard-answer');
  if (!textarea || !step) return;
  state.answers[step.key] = textarea.value.trim();
  persistDraft();
}

function nextWizardStep(allowEmpty: boolean) {
  saveCurrentStepAnswer();
  const step = WIZARD_STEPS[state.stepIndex];
  const errorEl = hostEl?.querySelector<HTMLElement>('#onboarding-wizard-error');

  if (!allowEmpty && !state.answers[step.key]) {
    if (errorEl) errorEl.textContent = 'Напишите короткую запись или выберите «Пропустить».';
    hostEl?.querySelector<HTMLTextAreaElement>('#onboarding-wizard-answer')?.focus();
    return;
  }

  if (!allowEmpty) {
    void trackOnboardingEvent({
      event_name: 'step_complete',
      step_key: step.key,
      step_index: state.stepIndex,
    });
  }

  if (state.stepIndex < WIZARD_STEPS.length - 1) {
    state.stepIndex += 1;
    renderWizard();
    return;
  }
  renderSummary();
}

function handleAction(action: string) {
  touchOnboardingActivity();
  switch (action) {
    case 'skip-video':
      leaveVideoScreen(true);
      break;
    case 'video-continue':
      leaveVideoScreen(false);
      break;
    case 'begin':
      void trackOnboardingEvent({ event_name: 'onboarding_start', entry_point: 'welcome' });
      renderExample();
      break;
    case 'back-welcome':
      renderWelcome();
      break;
    case 'start-wizard':
      state.stepIndex = 0;
      renderWizard();
      break;
    case 'wizard-next':
      nextWizardStep(false);
      break;
    case 'wizard-skip':
      void trackOnboardingEvent({
        event_name: 'skip',
        skip_type: 'step',
        step_key: WIZARD_STEPS[state.stepIndex]?.key,
        step_index: state.stepIndex,
      });
      nextWizardStep(true);
      break;
    case 'wizard-back':
      saveCurrentStepAnswer();
      if (state.stepIndex > 0) {
        state.stepIndex -= 1;
        renderWizard();
      } else {
        renderExample();
      }
      break;
    case 'edit-wizard':
      state.stepIndex = 0;
      renderWizard();
      break;
    case 'show-scales':
      showScreen('scales');
      break;
    case 'back-summary':
      renderSummary();
      break;
    case 'pause':
      saveCurrentStepAnswer();
      state.previousScreen = state.screen;
      void trackOnboardingEvent({
        event_name: 'onboarding_pause',
        last_step: WIZARD_STEPS[state.stepIndex]?.key || state.screen,
        step_index: state.screen === 'wizard' ? state.stepIndex : undefined,
      });
      showScreen('pause');
      break;
    case 'resume':
      if (state.previousScreen === 'wizard') renderWizard();
      else if (state.previousScreen === 'summary') renderSummary();
      else if (state.previousScreen === 'example') renderExample();
      else renderWelcome();
      break;
    case 'delete-draft':
      clearDraft();
      renderWelcome();
      break;
    case 'skip-guide':
      void trackOnboardingEvent({ event_name: 'skip', skip_type: 'guide' });
      markOnboardingSeen();
      hideOnboarding();
      callbacks?.onExitToCabinet();
      break;
    case 'open-cabinet':
      markOnboardingSeen();
      hideOnboarding();
      callbacks?.onExitToCabinet();
      break;
    case 'save-draft':
      void (async () => {
        persistDraft();
        if (callbacks?.onDraftComplete) {
          await callbacks.onDraftComplete({ ...state.answers });
        } else {
          markOnboardingSeen();
          hideOnboarding();
          callbacks?.onExitToCabinet();
        }
      })();
      break;
    case 'live-return-wizard':
      if (state.screen !== 'wizard') renderWizard();
      break;
  }
}

function bindHostEvents() {
  if (!hostEl) return;

  hostEl.addEventListener('click', (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLElement>('[data-onboarding-action]');
    if (!btn || !hostEl?.contains(btn)) return;
    handleAction(btn.dataset.onboardingAction || '');
  });

  const media = hostEl.querySelector<HTMLVideoElement | HTMLAudioElement>('#onboarding-video-media');
  media?.addEventListener('error', () => handleVideoLoadError());

  const textarea = hostEl.querySelector<HTMLTextAreaElement>('#onboarding-wizard-answer');
  textarea?.addEventListener('input', () => {
    const errorEl = hostEl?.querySelector<HTMLElement>('#onboarding-wizard-error');
    if (textarea.value.trim() && errorEl) errorEl.textContent = '';
  });
  textarea?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      nextWizardStep(false);
    }
  });

  const clarifyForm = hostEl.querySelector<HTMLFormElement>('#onboarding-clarify-form');
  clarifyForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = hostEl?.querySelector<HTMLInputElement>('#onboarding-clarify-input');
    const text = input?.value.trim() || '';
    if (!text) return;
    void submitClarifyQuestion(text);
    if (input) input.value = '';
  });

  const liveMicBtn = hostEl.querySelector<HTMLButtonElement>('#onboarding-live-mic');
  liveMicBtn?.addEventListener('click', () => startLiveVoiceInput());

  const liveTextForm = hostEl.querySelector<HTMLFormElement>('#onboarding-live-text-form');
  liveTextForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = hostEl?.querySelector<HTMLInputElement>('#onboarding-live-text-input');
    const text = input?.value.trim() || '';
    if (!text) return;
    void submitLiveDialogQuestion(text);
    if (input) input.value = '';
  });
}

export function renderOnboarding(cbs: OnboardingCallbacks) {
  callbacks = cbs;
  hostEl = $('journey-onboarding-host');
  if (!hostEl) return;

  checkOnboardingSessionTimeout();

  active = true;
  setContainerMode(true);
  hostEl.hidden = false;
  hostEl.classList.remove('hidden');
  hostEl.innerHTML = buildHostMarkup();
  hostEl.setAttribute('tabindex', '-1');
  bindHostEvents();
  if (shouldShowVideoExplainer()) renderVideo();
  else renderWelcome();
}

export function hideOnboarding() {
  active = false;
  setContainerMode(false);
  if (hostEl) {
    hostEl.hidden = true;
    hostEl.classList.add('hidden');
    hostEl.innerHTML = '';
  }
}

export function getOnboardingAnswers(): OnboardingAnswers {
  return { ...state.answers };
}

// Аватар-ассистент читает экран/шаг и триггерит визард через late-bound хост —
// подключаем на module evaluation, до первого renderOnboarding.
configureAssistantHost({
  getScreen: () => state.screen,
  getStepIndex: () => state.stepIndex,
  renderWizard,
});

// ADR-002 phase C: journey drives the onboarding overlay through the
// shared gateway; the implementation is registered here on module evaluation
// (the module is loaded from the composition root, main.ts).
registerOnboardingGateway({
  isActive: isOnboardingActive,
  hide: hideOnboarding,
  render: renderOnboarding,
  getSessionId: getOnboardingSessionId,
  markSeen: markOnboardingSeen,
  configureSessionTimeout: configureOnboardingSessionTimeout,
  // The gateway contract keeps video/avatar payloads opaque; narrow here.
  configureRuntime: (config) => configureOnboardingRuntime(config as Partial<OnboardingRuntimeConfig>),
});
